import type { MessageDescriptor } from '@lingui/core';
import { useMemo, useState, useCallback } from "react";
import { BaseEditableTable } from "./BaseEditableTable";
import type { Employee, EditableCellConfig, FilterConfig } from "./types";
import { Button } from "@/components/ui/button";
import { ContactRound } from "lucide-react";
import { employeeCSVColumns } from "@/utils/csvExport";
import type { ColumnFiltersState, RowSelectionState } from "@tanstack/react-table";
import { useNavigate } from "react-router";
import { useLingui } from "@lingui/react/macro";
import { useEnumOptions } from "@/hooks/settings/useEffectiveEnums";
import { useProfessionalRoleEnums } from "@/hooks/settings/useProfessionalRoleEnums";
import { useTableConfig } from "@/hooks/settings/useTableConfig";
import { getEmployeeColumns } from "@/utils/columnUtils";
import { openRecordColumn } from "@/utils/columnDefinitions";
import { LoginStatusBadge } from "@/components/employees/LoginStatusBadge";
import { useLabels } from "@/hooks/settings/useLabels";
import { useAuth } from "@clerk/clerk-react";
import { useVCardExport } from "@/hooks/useVCardExport";

// Add a helper alias representing the value type of any Employee field
type EmployeeFieldValue = Employee[keyof Employee];


interface EmployeeTableProps {
  data: Employee[];
  onCellUpdate?: (
    rowId: string,
    field: keyof Employee,
    newValue: EmployeeFieldValue
  ) => Promise<void>;
  enableSelection?: boolean;
  /** No data yet: skeleton rows instead of the empty state. */
  isLoading?: boolean;
  // Filter and search state owned by the page (URL)
  initialFilters?: ColumnFiltersState;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  searchTerm?: string;
  onSearchChange?: (search: string) => void;
  emptyState?: React.ReactNode;
  toolbarRight?: React.ReactNode;
}


export function EmployeeTable({
  data,
  onCellUpdate,
  enableSelection = false,
  isLoading = false,
  initialFilters,
  onFilterChange,
  searchTerm,
  onSearchChange,
  emptyState,
  toolbarRight,
}: EmployeeTableProps) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const { orgId } = useAuth();

  // Get custom enum options
  const { options: statusOptions } = useEnumOptions('employees', 'status');

  // Fetch available labels
  const { data: availableLabels = [] } = useLabels(orgId || "", "all");
  const { options: genderOptions } = useEnumOptions('employees', 'gender');
  const { options: qualificationOptions } = useEnumOptions('employees', 'qualification_level');
  // The role column is read-only and resolves its cell label from these
  // options, so they come from EVERY effective employees.role row (inactive
  // included): a contract keeps a role that was deactivated later, and the
  // cell must still show its label (issue #1668).
  const { options: roleOptions } = useProfessionalRoleEnums();

  // Get table configuration
  const { data: tableConfig } = useTableConfig('employees');

  // Define the columns for the employee table using the exported function.
  // A read-only "Login" display column (issue #354) is appended — it renders
  // LoginStatusBadge derived purely from returned employee fields (no per-row
  // Clerk call / N+1); any "Signed in" state is detail-only.
  const columns = useMemo<EditableCellConfig<Employee>[]>(
    () => [
      ...getEmployeeColumns(
        // Identical ternary branches on purpose (GroupTable.tsx precedent): `i18n._`'s
        // overloads cannot be resolved for a union argument; collapsing reintroduces TS2769.
        (template: string | MessageDescriptor): string =>
          typeof template === 'string' ? i18n._(template) : i18n._(template),
        navigate,
        {
        status: statusOptions,
        gender: genderOptions,
        qualificationLevel: qualificationOptions,
        role: roleOptions,
      }, { availableLabels }),
      {
        accessorKey: 'clerkUserId',
        id: 'login',
        header: t`table.employees.columns.login`,
        editable: false,
        sortable: false,
        cell: ({ row }) => (
          <LoginStatusBadge
            clerkUserId={row.original.clerkUserId}
            authRole={row.original.authRole}
          />
        ),
      },
      openRecordColumn<Employee>("/employees"),
    ],
    [t, i18n, navigate, statusOptions, genderOptions, qualificationOptions, roleOptions, availableLabels]
  );

  // Define filter configuration for each column (text filtering is handled by global search)
  const filterConfig: FilterConfig<Employee> = useMemo(() => ({
    status: 'select',
    gender: 'select',
    labels: 'select',
    qualificationLevel: 'select',
    role: 'select',
    birthdate: 'date',
    createdAt: 'date',
    // Text fields (firstname, lastname, email, phone) are handled by global search
  }), []);

  // CSV export lives in the table toolbar. The vCard export sends ids only.
  const { mutate: exportVCard, isPending: isVCardExporting } = useVCardExport("employees");

  const renderSelectionActions = useCallback(
    (selectedRowIds: string[], clearSelection: () => void) => (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportVCard(selectedRowIds)}
          disabled={isVCardExporting}
          title={t`table.employees.actions.exportVCardHint`}
          className="flex items-center space-x-2"
        >
          <ContactRound className="h-4 w-4" aria-hidden="true" />
          <span>{t`table.employees.actions.exportVCard`}</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={clearSelection}>
          {t`table.employees.actions.clearSelection`}
        </Button>
      </>
    ),
    [exportVCard, isVCardExporting, t]
  );

  // Default cell update handler if none provided
  const defaultCellUpdateHandler = async (
    _: string,
    __: keyof Employee,
    ___: EmployeeFieldValue
  ): Promise<void> => {
    // This is a stub that would be replaced with actual API call
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    // For production, replace with actual API call
    // Development/demo error simulation can be controlled via environment variable
    if (import.meta.env.DEV && Math.random() > 0.9) {
      throw new Error("Simulated API error");
    }

    // Success toast is handled in the mutation hook
  };

  // Prepare column visibility and order from table config
  const defaultColumnVisibility = useMemo(() => {
    if (!tableConfig?.visibleColumns) return undefined;
    
    // Create visibility state object from visibleColumns array
    const visibility: Record<string, boolean> = {};
    columns.forEach(col => {
      const key = col.id || String(col.accessorKey);
      visibility[key] = tableConfig.visibleColumns?.includes(key) ?? true;
    });
    visibility.open = true;
    return visibility;
  }, [tableConfig?.visibleColumns, columns]);

  const defaultColumnOrder = useMemo(() => {
    return tableConfig?.columnOrder || undefined;
  }, [tableConfig?.columnOrder]);

  return (
    <BaseEditableTable<Employee>
      data={data}
      columns={columns}
      onCellUpdate={onCellUpdate || defaultCellUpdateHandler}
      defaultSortField={(tableConfig?.defaultSortColumn as keyof Employee) || "firstname"}
      defaultSortOrder={tableConfig?.defaultSortDesc ? 'desc' : 'asc'}
      enableSelection={enableSelection}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      renderSelectionActions={renderSelectionActions}
      enableSearch={true}
      searchPlaceholder={t`table.employees.search.placeholder`}
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      enableFilters={true}
      filterConfig={filterConfig}
      onFilterChange={onFilterChange}
      defaultColumnFilters={initialFilters}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="employee-table"
      defaultColumnVisibility={defaultColumnVisibility}
      defaultColumnOrder={defaultColumnOrder}
      isLoading={isLoading}
      enableExport={true}
      exportColumns={employeeCSVColumns}
      exportFileName="employees"
      emptyState={emptyState}
      toolbarRight={toolbarRight}
    />
  );
}